import {
  Shield,
  UserPlus,
  CreditCard,
  FileCheck,
  ArrowRightLeft,
  ClipboardCheck,
  HelpCircle,
  Plus,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";

export interface ContextDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  requiredConnectors: string[];
}

/** All available contexts — mirrors packages/agent-runtime/src/contexts/registry.ts */
export const AVAILABLE_CONTEXTS: ContextDef[] = [
  {
    id: "insurance_verification",
    name: "Insurance Verification",
    description: "Real-time eligibility checks, benefit lookup, and coverage confirmation via Availity and X12 270/271",
    icon: "Shield",
    color: "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800",
    requiredConnectors: ["payer_eligibility", "availity", "ehr_nextgen"],
  },
  {
    id: "patient_intake",
    name: "Patient Intake",
    description: "New patient registration, demographic collection, insurance capture, and EHR record creation",
    icon: "UserPlus",
    color: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/20 dark:border-emerald-800",
    requiredConnectors: ["ehr_nextgen", "payer_eligibility", "sms", "email_ses"],
  },
  {
    id: "billing",
    name: "Billing & Claims",
    description: "Claim submission (837P), status tracking (276/277), ERA processing (835), and denial management",
    icon: "CreditCard",
    color: "text-violet-600 bg-violet-50 border-violet-200 dark:text-violet-400 dark:bg-violet-900/20 dark:border-violet-800",
    requiredConnectors: ["claim_submission", "claim_status", "era_processing", "waystar", "ehr_nextgen"],
  },
  {
    id: "prior_auth",
    name: "Prior Authorization",
    description: "Automated PA submission, status tracking, peer-to-peer scheduling, and appeal generation",
    icon: "FileCheck",
    color: "text-amber-600 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-800",
    requiredConnectors: ["prior_auth", "availity", "ehr_nextgen", "fax"],
  },
  {
    id: "referral",
    name: "Referral Management",
    description: "Specialist referral creation, PA check, clinical document transmission, and status tracking",
    icon: "ArrowRightLeft",
    color: "text-cyan-600 bg-cyan-50 border-cyan-200 dark:text-cyan-400 dark:bg-cyan-900/20 dark:border-cyan-800",
    requiredConnectors: ["ehr_nextgen", "prior_auth", "fax", "direct_messaging"],
  },
  {
    id: "claims_review",
    name: "Claims Review",
    description: "Pre-submission claim audit, coding validation, denial pattern analysis, and appeal drafting",
    icon: "ClipboardCheck",
    color: "text-rose-600 bg-rose-50 border-rose-200 dark:text-rose-400 dark:bg-rose-900/20 dark:border-rose-800",
    requiredConnectors: ["claim_submission", "claim_status", "era_processing", "ehr_nextgen"],
  },
];

const CONTEXT_ICONS: Record<string, React.ReactNode> = {
  Shield: <Shield className="h-4 w-4" />,
  UserPlus: <UserPlus className="h-4 w-4" />,
  CreditCard: <CreditCard className="h-4 w-4" />,
  FileCheck: <FileCheck className="h-4 w-4" />,
  ArrowRightLeft: <ArrowRightLeft className="h-4 w-4" />,
  ClipboardCheck: <ClipboardCheck className="h-4 w-4" />,
};

interface ContextPickerProps {
  value: string[];
  onChange: (contextIds: string[]) => void;
}

/**
 * Tab content for selecting composable contexts to attach to an agent node.
 * Used in NodeConfigDrawer's Contexts tab.
 */
export function ContextPicker({ value, onChange }: ContextPickerProps) {
  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((c) => c !== id) : [...value, id]);
  };

  const attached = AVAILABLE_CONTEXTS.filter((c) => value.includes(c.id));
  const available = AVAILABLE_CONTEXTS.filter((c) => !value.includes(c.id));

  return (
    <div className="space-y-4">
      <p className="text-[10px] text-muted-foreground">
        Contexts inject domain knowledge, tools, and rules into this agent automatically. Attached contexts merge their connectors and system prompt fragments at compile time.
      </p>

      {/* Attached contexts */}
      {attached.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Attached ({attached.length})
          </p>
          <div className="space-y-1.5">
            {attached.map((ctx) => (
              <div
                key={ctx.id}
                className={cn(
                  "flex items-start gap-2 rounded-lg border px-3 py-2",
                  ctx.color,
                )}
              >
                <span className="mt-0.5 shrink-0">
                  {CONTEXT_ICONS[ctx.icon] ?? <HelpCircle className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold">{ctx.name}</p>
                  <p className="text-[10px] opacity-80 mt-0.5">{ctx.description}</p>
                  <p className="text-[10px] opacity-60 mt-1">
                    Connectors: {ctx.requiredConnectors.join(", ")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(ctx.id)}
                  className="mt-0.5 shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                  title="Remove context"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available contexts */}
      {available.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Available
          </p>
          <div className="space-y-1.5">
            {available.map((ctx) => (
              <button
                key={ctx.id}
                type="button"
                onClick={() => toggle(ctx.id)}
                className="w-full flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-left hover:border-border/80 hover:bg-muted/50 transition-colors"
              >
                <span className="mt-0.5 shrink-0 text-muted-foreground">
                  {CONTEXT_ICONS[ctx.icon] ?? <HelpCircle className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-foreground">{ctx.name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{ctx.description}</p>
                </div>
                <Plus className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      {AVAILABLE_CONTEXTS.length === 0 && (
        <p className="text-[11px] text-muted-foreground text-center py-4">No contexts available</p>
      )}
    </div>
  );
}
